<template>
  <FormSection
    :title="$t('APP.STATS_BUILDER.TITLE')"
    :hint="$t('HINTS.APPS.STATS')"
  >
    <StatsBuilderFilters
      :event-bus="eventBus"
      :types="types"
      :ranges="ranges"
      :default-filters="defaultFilters"
      :loading="loadingConfig"
      @create="createReport"
    />

    <template v-if="showReport">
      <StatsBuilderChart
        :primary-type="primaryType"
        :secondary-type="secondaryType"
        :range="range"
      />
      <div class="row">
        <div
          v-for="tag in tableTags"
          :key="tag.type + '_' + tag.tag"
          class="columns small-12"
          :class="tablesDynamicClasses"
        >
          <StatsBuilderTable :tag="tag" :range="range" />
        </div>
      </div>
    </template>
  </FormSection>
</template>

<script>
import Vue from 'vue';

import StatsBuilderFilters from './StatsBuilderFilters';
import StatsBuilderChart from './StatsBuilderChart';
import StatsBuilderTable from './StatsBuilderTables';

import FormSection from '@@/common/layout/FormSection';

import packageName from '@/mixins/packageName';
import permissions from '@/mixins/permissions';
import packageNameWatch from '@/mixins/apps/packageNameWatch';
import { requestQueue } from '@/api';
import { Insight } from '@/api/api';
import { mapGetters, mapActions } from 'vuex';
import { APP_DETAILS, APP_CHARTS_CONFIG } from '@/store/types';
import checkStatisticsPermission from '@/routes/before-middlewares/checkStatisticsPermission';

export default {
  name: 'StatsBuilder',
  components: {
    FormSection,
    StatsBuilderFilters,
    StatsBuilderChart,
    StatsBuilderTable,
  },
  mixins: [packageNameWatch, packageName, permissions],
  data() {
    return {
      eventBus: new Vue(),
      primaryType: {},
      secondaryType: {},
      range: '',
      tableTags: [],
      showReport: false,
      loadingConfig: true,
      types: [],
      ranges: [],
      defaultFilters: null,
    };
  },

  computed: {
    ...mapGetters({
      app: APP_DETAILS,
    }),
    tablesDynamicClasses() {
      return {
        'large-6': this.tableTags && this.tableTags.length > 1,
        'large-12': this.tableTags && this.tableTags.length === 1,
      };
    },
  },

  watch: {
    app() {
      this.getChartsConfig();
    },
  },

  beforeRouteEnter: checkStatisticsPermission,
  beforeRouteUpdate: checkStatisticsPermission,

  created() {
    this.getChartsConfig();
  },

  methods: {
    getInfo() {
      this.eventBus.$emit('resetFields');
      this.primaryType = {};
      this.secondaryType = {};
      this.range = '';
      this.tableTags = [];
      this.showReport = false;
    },

    createReport({ range, primaryType, secondaryType, tableTags }) {
      this.primaryType = primaryType;
      this.secondaryType = secondaryType;
      this.range = range;
      this.tableTags = tableTags;
      this.showReport = true;
      // TODO: it should move to where the request made
      // but beacuse requestQueue.flush() cancel all requests
      // it may cancel unwanted request
      requestQueue.flush();
    },

    getChartsConfig() {
      const isAppLoaded = this.app && this.app.packageName;
      if (!isAppLoaded) return;
      this.loadingConfig = true;
      const { config: defaultConfig } = this.$route.params;
      return Insight.getFilters({
        app: this.app,
        packageName: this.packageName,
        defaults: defaultConfig,
      })
        .then(({ types, ranges, defaults, chartsConfig }) => {
          this.types = types;
          this.ranges = ranges;
          this.defaultFilters = defaults;

          this.setChartConfigs(chartsConfig);
        })
        .catch(({ body: { message } = {} }) => {
          const errorMsg = message || this.$t('API_ERROR');
          this.$message.error(errorMsg);
        })
        .finally(() => {
          this.loadingConfig = false;
        });
    },

    ...mapActions({
      setChartConfigs: APP_CHARTS_CONFIG,
    }),
  },
};
</script>
